import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from './client.ts';
import type {
  Measurement,
  Medication,
  MedicationEvent,
  MeResponse,
  Milestone,
} from '@shared/types.ts';

type StateResponse = {
  me: MeResponse;
  measurements: Measurement[];
  milestones: Milestone[];
  medications: Medication[];
  medication_events: MedicationEvent[];
};

/** Cold-start aggregator. One round-trip seeds the cache for /me,
 *  measurements, milestones and medications so secondary tabs paint
 *  immediately on first navigation. Subsequent reads use the existing
 *  per-endpoint queries which refresh in the background. */
export function useBootState() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['state'],
    queryFn: () => api<StateResponse>('/api/state'),
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!q.data) return;
    const { me, measurements, milestones, medications, medication_events } = q.data;
    qc.setQueryData(['me'], me);
    const babyId = me.active_baby_id;
    if (babyId) {
      qc.setQueryData(['measurements', { babyId, kind: null }], { measurements });
      qc.setQueryData(['milestones', { babyId }], { milestones });
      qc.setQueryData(['medication_events', { babyId }], { events: medication_events });
    }
    qc.setQueryData(['medications'], { medications });
  }, [q.data, qc]);

  return q;
}

/** Read the cached /me. After useBootState has run once, this is always a
 *  cache hit. */
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/me'),
    retry: false,
    staleTime: 30_000,
  });
}
